import { useEffect, useState } from "react";
import useAuth from "./useAuth";
import { useSocket } from "../context/SocketContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { fetchFreelancerJobs, findJobs } from "../services/api/workOrder";
import { useUpdateWorkOrder } from "./useUpdateWorkOrder";
import { isVendorTech } from "../utils/helpers";

export function useWorkOrderSocket(workOrderId) {
  const { refetchUserAllowedAction, refetchWorkOrderPaymetDetails } = useUpdateWorkOrder();
  const { auth, authRole } = useAuth();
  const socket = useSocket();
  const [jobsFilters, setJobsFilters] = useState({
    status: "AVAILABLE",
    isCounter: false,
  });
  const queryClient = useQueryClient();
  const engineerFilters = useSelector((state) => state.filters.value);
  const [workOrderUpdates, setWorkOrderUpdates] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [userAllowedActionUpdates, setUserAllowedActionUpdates] = useState(null);
  const [accessRevoked, setAccessRevoked] = useState(false);

  const { mutateAsync: fetchFreelancerJobsMutation } = useMutation({
    mutationFn: fetchFreelancerJobs,
  });

  const {
    isPending: isAvailableJobsLoading,
    error: isWorkOrderError,
    data: jobsData,
    refetch: fetchJobs,
  } = useQuery({
    queryKey: [
      "findJobs",
      {
        page: engineerFilters?.pagination?.page,
        pageSize: engineerFilters?.pagination?.pageSize,
        search: engineerFilters.search || "",
        startDate: engineerFilters.startDate,
        endDate: engineerFilters.endDate,
        minRate: engineerFilters.minRate,
        maxRate: engineerFilters.maxRate,
        minRequests: engineerFilters.minRequests,
        maxRequests: engineerFilters.maxRequests,
        vendorId: engineerFilters.vendorId,
        categoryId: engineerFilters.categoryId,
        subCategoryIds: engineerFilters.subCategoryIds,
        paymentType: engineerFilters.paymentType,
      },
    ],
    queryFn: () =>
      findJobs({
        latitude: engineerFilters?.coverageAreaCoordinates?.latitude,
        longitude: engineerFilters?.coverageAreaCoordinates?.longitude,
        radius: engineerFilters?.coverageAreaRadius,
        page: engineerFilters?.pagination?.page + 1 || 0,
        limit: engineerFilters?.pagination?.pageSize || 10,
        search: engineerFilters.search || "",
        startDate: engineerFilters.startDate,
        endDate: engineerFilters.endDate,
        minRate: engineerFilters.minRate,
        maxRate: engineerFilters.maxRate,
        minRequests: engineerFilters.minRequests,
        maxRequests: engineerFilters.maxRequests,
        categoryId: engineerFilters.categoryId,
        vendorId: engineerFilters.vendorId,
        subCategoryIds: engineerFilters.subCategoryIds,
        paymentType: engineerFilters.paymentType,
        isCoverageApplied: !!engineerFilters?.coverageAreaSearchText,
      }),
    enabled:
      !!engineerFilters?.coverageAreaCoordinates.latitude && jobsFilters.status === "AVAILABLE",
  });

  useEffect(() => {
    async function getFreelancerJobs() {
      try {
        const response = await fetchFreelancerJobsMutation({
          latitude: engineerFilters?.coverageAreaCoordinates?.latitude,
          longitude: engineerFilters?.coverageAreaCoordinates?.longitude,
          radius: engineerFilters?.coverageAreaRadius,
          startDate: engineerFilters?.startDate,
          endDate: engineerFilters?.endDate,
          minRate: engineerFilters?.minRate,
          maxRate: engineerFilters?.maxRate,
          minRequests: engineerFilters?.minRequests,
          maxRequests: engineerFilters?.maxRequests,
          categoryId: engineerFilters?.categoryId,
          vendorId: engineerFilters?.vendorId,
          subCategoryIds: engineerFilters?.subCategoryIds,
          paymentType: engineerFilters?.paymentType,
          status: jobsFilters.status,
          isCounter: jobsFilters.isCounter,
        });
        setJobs(response.data);
      } catch (error) {
        console.error("Error fetching freelancer jobs:", error);
      }
    }
    if (engineerFilters?.coverageAreaCoordinates?.latitude) {
    }

    if (jobsFilters.status !== "AVAILABLE") {
      getFreelancerJobs();
    }
  }, [, fetchFreelancerJobsMutation]);

  useEffect(() => {
    if (jobsData?.data?.workOrders) {
      setJobs(jobsData?.data?.workOrders);
    }
  }, [jobsData]);

  /* useEffect(() => {
    if (!auth?.user?.id || !authRole || !workOrderId) return;

    socket.emit("subscribeWorkOrder", {
      engineerId: auth.user.id,
      workOrderId,
    });

    socket.on(`workOrder:update:${workOrderId}`, (workOrderData) => {
      console.log("🔄 Work Order Update Received:", workOrderData);

      if (workOrderData?.forceUpdate) {
        await updateWorkOrder();
        return;
      }

      setWorkOrderUpdates((prev) => ({
        ...prev,
        ...workOrderData.updatedWorkOrder,
      }));

      setUserAllowedActionUpdates((prev) => ({
        ...prev,
        ...workOrderData.updatedUserAllowedAction,
      }));
    });
  }, [auth, workOrderId]); */

  useEffect(() => {
    if (!socket || !auth?.user?.id || !authRole || !workOrderId) return;

    // Subscribe to the work order
    socket.emit("subscribeWorkOrder", {
      engineerId: auth.user.id,
      workOrderId,
    });

    // Define the event name
    const eventName = `workOrder:update:${workOrderId}`;

    // Async handler for socket updates
    const handleWorkOrderUpdate = async (workOrderData) => {
      console.log("🔄 Work Order Update Received:", workOrderData);

      try {
        if (workOrderData?.forceUpdate) {
          await refetchUserAllowedAction();
          // `refetch` ignores the query's `enabled`, so the vendor tech check is
          // repeated here to keep the payment call off their side entirely.
          if (!isVendorTech(auth?.user)) {
            await refetchWorkOrderPaymetDetails();
          }
          queryClient.invalidateQueries({ queryKey: ["jobDetail"] });
          return;
        }

        if (workOrderData?.updatedWorkOrder) {
          setWorkOrderUpdates((prev) => ({
            ...prev,
            ...workOrderData.updatedWorkOrder,
          }));
        }

        if (workOrderData?.updatedUserAllowedAction) {
          setUserAllowedActionUpdates((prev) => ({
            ...prev,
            ...workOrderData.updatedUserAllowedAction,
          }));
        }
      } catch (error) {
        console.error("Error handling work order update:", error);
      }
    };

    // Listen for updates
    socket.on(eventName, handleWorkOrderUpdate);

    // Cleanup to avoid duplicate listeners
    return () => {
      socket.off(eventName, handleWorkOrderUpdate);
    };
  }, [socket, auth?.user?.id, authRole, workOrderId, refetchWorkOrderPaymetDetails]);

  useEffect(() => {
    if (!socket || !auth?.user?.id) return;

    // ✅ Subscribe user to their own WebSocket channel
    socket.emit("subscribeUser", { userId: auth.user.id });

    // Listen for updates on the jobs list.
    socket.on("workOrder:list:update", (updatePayload) => {
      const { id, status } = updatePayload;
      if (jobsFilters.status == "AVAILABLE") {
        setJobs((prevJobs) => prevJobs.filter((job) => job.id !== id));
      } else {
        setJobs((prevJobs) =>
          prevJobs.map((job) => (job.id === id ? { ...job, ...updatePayload } : job)),
        );
      }

      console.log(jobsFilters);
      // Otherwise, merge the update into the existing job details.
    });

    socket.on("workOrder:list:remove", (workOrder) => {
      console.log("remove ", workOrder);
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== workOrder.id));
    });

    // ✅ Listen for work order updates
    socket.on("workOrder:published", (newWorkOrder) => {
      setJobs((prevJobs) => [newWorkOrder, ...prevJobs]);
    });

    return () => {
      socket.off("workOrder:list:remove");
      socket.off("workOrder:list:update");
      socket.off("workOrder:published");
    };
  }, [socket, auth]);

  return {
    workOrderUpdates,
    userAllowedActionUpdates,
    accessRevoked,
    socket,
    jobs,
    setJobsFilters,
    jobsFilters,
    isAvailableJobsLoading,
  };
}
